/* eslint-disable max-lines */
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { drugProfilesByNdcApi } from 'api/drug';
import { listInventoryByNdcApi } from 'api/inventory';
import { updateFillHistoryApi } from 'api/prescription';
import { generateBulkLabelsApi } from 'api/requests';
import ReshippingIcon from 'common/components/ReshippingIcon';
import orderFillType from 'common/constants/orderFillType';
import useRoles from 'common/hooks/useRoles';
import { EnableInventoryLookup } from 'common/utils';
import { OrderFillType } from 'enums/common.enum';
import { handleError } from 'store/thunks/errorHandlerThunks';

const FillHistoryDialog = ({ open, onClose, fillHistory, loading, onUpdateFillHistory }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { editFillHistory, generateBulkLabels } = useRoles();
  const [expanded, setExpanded] = useState([]);
  const [initialExpandDone, setInitialExpandDone] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [overRideRows, setOverRideRows] = useState([]);
  const [activeRowId, setActiveRowId] = useState(null);
  const [ndcSearch, setNdcSearch] = useState('');
  const [drugProfiles, setDrugProfiles] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      setExpanded([]);
      setInitialExpandDone(false);
      onClose();
    }
  };

  const handleRowToggle = (panelId) => () => {
    setExpanded((prevExpanded) =>
      prevExpanded.includes(panelId)
        ? prevExpanded.filter((id) => id !== panelId)
        : [...prevExpanded, panelId]
    );
  };

  const groupedTransactions = useMemo(() => {
    const fillHistoryData = fillHistory?.results ?? [];
    if (!fillHistoryData.length) return [];

    const groups = {};

    fillHistoryData.forEach((entry) => {
      const fillDate = entry.fillDate ? format(new Date(entry.fillDate), 'MM/dd/yyyy') : 'N/A';
      const fillType = entry.fillType || 'UNKNOWN';
      const currentIsReshipment = entry.isReshipment ?? false;
      const orderId = entry.orderId || 'N/A';
      const parentOrderId = entry.parentOrderId || null;

      const groupKey = `${fillDate}_${fillType}_${orderId}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          fillDate,
          fillType,
          isReshipment: currentIsReshipment,
          rxNumber: entry.rxNumber || 'N/A',
          orderId,
          parentOrderId,
          prescribedQuantity: entry.prescribedQuantity ?? 'N/A',
          transactions: [],
          netQuantity: 0,
          groupKey,
          lineItemId: entry.lineItemId || null,
        };
      }

      const entryQuantity = parseFloat(entry.quantity) || 0;
      groups[groupKey].transactions.push({ ...entry, quantity: entryQuantity });
      groups[groupKey].netQuantity += entryQuantity;
      groups[groupKey].isReshipment = groups[groupKey].isReshipment && currentIsReshipment;
      if (parentOrderId && !groups[groupKey].parentOrderId) {
        groups[groupKey].parentOrderId = parentOrderId;
      }

      if (!groups[groupKey].lineItemId && entry.lineItemId && !entry.isVoided) {
        groups[groupKey].lineItemId = entry.lineItemId;
      }
    });

    const sortedGroups = Object.values(groups).sort((a, b) => {
      if (a.fillDate === 'N/A' && b.fillDate === 'N/A') return 0;
      if (a.fillDate === 'N/A') return 1;
      if (b.fillDate === 'N/A') return -1;

      const dateA = new Date(a.fillDate);
      const dateB = new Date(b.fillDate);
      return dateB - dateA;
    });

    let cumulativeQuantity = 0;
    for (let i = sortedGroups.length - 1; i >= 0; i -= 1) {
      cumulativeQuantity += sortedGroups[i].netQuantity;
      sortedGroups[i].totalDispensed = cumulativeQuantity;
    }

    return sortedGroups;
  }, [fillHistory]);

  const handleViewLabel = async (e, lineItemId) => {
    e.stopPropagation();
    if (!lineItemId) return;

    const snackbarKey = enqueueSnackbar('Downloading label...', { variant: 'info' });
    try {
      const labelUrl = await generateBulkLabelsApi({ requestLineItemIds: [lineItemId] });
      if (labelUrl) {
        closeSnackbar(snackbarKey);
        window.open(labelUrl, '_blank');
      }
    } catch (error) {
      closeSnackbar(snackbarKey);
      dispatch(handleError({ error }));
      enqueueSnackbar('Failed to download label', { variant: 'error' });
    }
  };

  const getFillTypeColor = (fillType) => {
    if (fillType === OrderFillType.REFILL) {
      return theme.palette.info.dark;
    }
    if (fillType === OrderFillType.FIRST_FILL) {
      return theme.palette.success.dark;
    }
    return theme.palette.grey[500];
  };

  const getTransactionReason = (transaction) => {
    if (transaction.isVoided) {
      return 'Voided';
    }

    if (transaction.isEditedPostShipment) {
      return 'Edited after shipment';
    }

    if (transaction.isReshipment) {
      if (transaction.quantity > 0) {
        return 'Reshipment Filled';
      }
      if (transaction.quantity !== undefined && transaction.quantity !== null) {
        return 'Reshipment requested';
      }
    }

    return 'Filled';
  };

  useEffect(() => {
    if (!loading && groupedTransactions.length > 0 && !initialExpandDone) {
      setExpanded([groupedTransactions[0].groupKey]);
      setInitialExpandDone(true);
    }
  }, [loading, groupedTransactions, initialExpandDone]);

  useEffect(() => {
    if (groupedTransactions.length === 0) {
      setInitialExpandDone(false);
    }
  }, [groupedTransactions.length]);

  const makeEmptyRow = () => ({
    id: `row-${Date.now()}-${Math.random()}`,
    ndc: '',
    drugName: '',
    quantity: '',
  });

  const handleEditNdc = (transaction) => (e) => {
    e.stopPropagation();
    setSelectedTransaction(transaction);
    const initialRows =
      transaction.overrides?.length > 0
        ? transaction.overrides.map((o) => ({
            id: `row-${Date.now()}-${Math.random()}`,
            ndc: o.ndc || '',
            drugName: '',
            quantity: o.quantity != null ? String(o.quantity) : '',
          }))
        : [
            {
              id: `row-${Date.now()}-${Math.random()}`,
              ndc: transaction.ndc || '',
              drugName: '',
              quantity: transaction.quantity != null ? String(transaction.quantity) : '',
            },
          ];
    setOverRideRows(initialRows);
    setActiveRowId(null);
    setNdcSearch('');
    setDrugProfiles([]);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = (event, reason) => {
    if (updateLoading || reason === 'backdropClick') return;
    setEditDialogOpen(false);
    setSelectedTransaction(null);
    setOverRideRows([]);
    setActiveRowId(null);
    setNdcSearch('');
    setDrugProfiles([]);
  };

  const debouncedSearchRef = useRef(
    debounce(async (ndc) => {
      if (ndc.length >= 3) {
        setSearchLoading(true);
        try {
          // POM-5818: source NDC / drug-name search from inventory when the switch
          // is on; fall back to the legacy drug_profiles search otherwise.
          const searchApi = EnableInventoryLookup ? listInventoryByNdcApi : drugProfilesByNdcApi;
          const results = await searchApi(ndc);
          setDrugProfiles(results || []);
        } catch {
          setDrugProfiles([]);
        } finally {
          setSearchLoading(false);
        }
      } else {
        setDrugProfiles([]);
      }
    }, 300)
  );

  useEffect(() => {
    debouncedSearchRef.current(ndcSearch);
  }, [ndcSearch]);

  const handleRowNdcInput = (rowId, value) => {
    setActiveRowId(rowId);
    setNdcSearch(value);
    setOverRideRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ndc: value, drugName: '' } : r))
    );
    setDrugProfiles([]);
  };

  const handleSelectDrugForRow = (rowId, drug) => {
    setOverRideRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ndc: drug.ndc, drugName: '' } : r))
    );
    setActiveRowId(null);
    setNdcSearch('');
    setDrugProfiles([]);
  };

  const handleRowQuantityChange = (rowId, value) => {
    setOverRideRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, quantity: value } : r)));
  };

  const handleAddRow = () => {
    setOverRideRows((prev) => [...prev, makeEmptyRow()]);
  };

  const handleRemoveRow = (rowId) => {
    setOverRideRows((prev) => prev.filter((r) => r.id !== rowId));
    if (activeRowId === rowId) {
      setActiveRowId(null);
      setNdcSearch('');
      setDrugProfiles([]);
    }
  };

  const ndcCounts = overRideRows.reduce((acc, r) => {
    if (r.ndc) acc[r.ndc] = (acc[r.ndc] || 0) + 1;
    return acc;
  }, {});
  const hasDuplicateNdc = Object.values(ndcCounts).some((c) => c > 1);

  const isSubmitDisabled =
    overRideRows.length === 0 || overRideRows.some((r) => !r.ndc || !r.quantity) || hasDuplicateNdc;

  const handleUpdateNdc = async () => {
    if (!selectedTransaction || isSubmitDisabled) return;
    setUpdateLoading(true);
    try {
      const result = await updateFillHistoryApi({
        fillHistoryId: selectedTransaction.fillHistoryId,
        overRides: overRideRows.map((r) => ({ ndcCode: r.ndc, quantity: r.quantity })),
      });
      if (result) {
        enqueueSnackbar('NDC updated successfully', { variant: 'success' });
        if (onUpdateFillHistory) {
          const updatedNdc =
            overRideRows.length === 1 ? overRideRows[0].ndc : selectedTransaction.ndc;
          onUpdateFillHistory(selectedTransaction.fillHistoryId, updatedNdc);
        }
        handleCloseEditDialog();
      }
    } catch {
      enqueueSnackbar('Failed to update NDC', { variant: 'error' });
    } finally {
      setUpdateLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: '70%',
          height: '80%',
          maxWidth: 'none',
          maxHeight: 'none',
        },
      }}
    >
      <DialogTitle>
        <Grid container justifyContent='space-between' alignItems='center'>
          <Box>
            <Typography variant='h5' sx={{ fontWeight: 600 }}>
              Fill History
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
              Net fills grouped by date and type. Expand rows to view transaction details.
            </Typography>
            {!loading && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                Total Results: {groupedTransactions.length}
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label='Close Fill History Dialog'>
            <CloseIcon />
          </IconButton>
        </Grid>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ overflow: 'auto' }}>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant='body1' color='text.secondary'>
              Loading fill history...
            </Typography>
          </Box>
        )}

        {!loading && fillHistory?.count === 0 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '200px',
            }}
          >
            <Typography variant='h6' color='text.secondary'>
              No fill history found
            </Typography>
          </Box>
        )}

        {!loading && groupedTransactions.length > 0 && (
          <TableContainer
            component={Paper}
            variant='outlined'
            sx={{ borderRadius: 2, overflow: 'hidden' }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.light' }}>
                  <TableCell sx={{ width: '50px' }} />
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Order ID
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Fill Date
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Reshipment
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Fill Type
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    RX Number
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Prescribed Quantity
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Quantity
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center'>
                    Total Dispensed
                  </TableCell>
                  {generateBulkLabels && (
                    <TableCell sx={{ fontWeight: 600, color: 'white' }} align='center' />
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedTransactions.map((group) => {
                  const panelId = group.groupKey;
                  const isExpanded = expanded.includes(panelId);

                  return (
                    <>
                      <TableRow
                        key={panelId}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          },
                        }}
                        onClick={handleRowToggle(panelId)}
                      >
                        <TableCell>
                          <IconButton size='small'>
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell align='center'>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography variant='body2'>{group.orderId}</Typography>
                            {group.isReshipment && (
                              <Tooltip
                                title={
                                  group.parentOrderId
                                    ? `Reshipment of ${group.parentOrderId}`
                                    : 'Reshipment'
                                }
                                arrow
                                placement='top'
                              >
                                <span>
                                  <ReshippingIcon color={theme.palette.info.main} />
                                </span>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {group.fillDate}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>
                            {group.isReshipment ? 'Yes' : 'No'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={orderFillType[group.fillType] || group.fillType}
                            size='small'
                            sx={{
                              backgroundColor: getFillTypeColor(group.fillType),
                              color: theme.palette.primary.contrastText,
                            }}
                          />
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>{group.rxNumber}</Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography variant='body2'>
                            {group.prescribedQuantity ?? 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: group.netQuantity >= 0 ? 'success.main' : 'error.main',
                            }}
                          >
                            {group.netQuantity}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,
                              color: group.totalDispensed >= 0 ? 'success.main' : 'error.main',
                            }}
                          >
                            {group.totalDispensed}
                          </Typography>
                        </TableCell>
                        {generateBulkLabels && (
                          <TableCell align='center' onClick={(e) => e.stopPropagation()}>
                            {group.lineItemId ? (
                              <Button
                                variant='text'
                                size='small'
                                onClick={(e) => handleViewLabel(e, group.lineItemId)}
                                sx={{
                                  textTransform: 'none',
                                  textDecoration: 'underline',
                                  color: 'primary.main',
                                  minWidth: 'auto',
                                  padding: '4px 8px',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    backgroundColor: 'transparent',
                                  },
                                }}
                              >
                                View Label
                              </Button>
                            ) : (
                              <span />
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={generateBulkLabels ? 10 : 9}
                          sx={{
                            py: 0,
                            px: 0,
                            borderBottom: isExpanded ? 1 : 0,
                            borderColor: 'divider',
                          }}
                        >
                          <Collapse in={isExpanded} timeout='auto'>
                            <TableContainer>
                              <Table size='small'>
                                <TableHead>
                                  <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                    <TableCell />
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      NDC
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      Reshipment
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      Transaction Date
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      Voided
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      Voided Date
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      Dispensed Quantity
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 600 }} align='center'>
                                      Reason
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.transactions.map((transaction) => {
                                    const getTransactionDate = () => {
                                      if (transaction.createdTimestamp) {
                                        return format(
                                          new Date(transaction.createdTimestamp),
                                          'MM/dd/yyyy'
                                        );
                                      }
                                      if (transaction.fillDate) {
                                        return format(new Date(transaction.fillDate), 'MM/dd/yyyy');
                                      }
                                      return 'N/A';
                                    };

                                    const uniqueKey = `${transaction.lineItemId}-${
                                      transaction.createdTimestamp ||
                                      transaction.fillDate ||
                                      transaction.orderId
                                    }`;

                                    const isVoided = transaction.isVoided === true;
                                    const reasonDisplay = getTransactionReason(transaction);

                                    const hasOverrides = transaction.overrides?.length > 0;

                                    const struckStyle = hasOverrides
                                      ? { textDecoration: 'line-through', color: 'text.disabled' }
                                      : {};

                                    const quantityColor = () => {
                                      if (hasOverrides) return 'text.disabled';
                                      if (transaction.quantity >= 0) return 'success.main';
                                      return 'error.main';
                                    };

                                    return (
                                      <>
                                        <TableRow key={uniqueKey} sx={{ backgroundColor: 'white' }}>
                                          <TableCell />
                                          <TableCell align='center'>
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                              }}
                                            >
                                              <Typography variant='body2' sx={struckStyle}>
                                                {transaction.originalNdc ||
                                                  transaction.ndc ||
                                                  'N/A'}
                                              </Typography>
                                              {transaction.fillHistoryId &&
                                                editFillHistory &&
                                                !hasOverrides &&
                                                transaction.quantity >= 0 && (
                                                  <Tooltip title='Edit NDC' arrow placement='top'>
                                                    <IconButton
                                                      size='small'
                                                      onClick={handleEditNdc(transaction)}
                                                      sx={{
                                                        padding: '2px',
                                                        '&:hover': {
                                                          cursor: 'pointer',
                                                        },
                                                      }}
                                                    >
                                                      <EditIcon sx={{ fontSize: '16px' }} />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                            </Box>
                                          </TableCell>
                                          <TableCell align='center'>
                                            <Typography variant='body2' sx={struckStyle}>
                                              {transaction.isReshipment ? 'Yes' : 'No'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center'>
                                            <Typography variant='body2' sx={struckStyle}>
                                              {getTransactionDate()}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center'>
                                            <Typography variant='body2' sx={struckStyle}>
                                              {isVoided ? 'Yes' : 'No'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center'>
                                            <Typography variant='body2' sx={struckStyle}>
                                              {transaction.voidedTimestamp
                                                ? format(
                                                    new Date(transaction.voidedTimestamp),
                                                    'MM/dd/yyyy'
                                                  )
                                                : '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell
                                            align='center'
                                            sx={{
                                              fontWeight: 500,
                                              color: quantityColor(),
                                            }}
                                          >
                                            <Box
                                              sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                              }}
                                            >
                                              <Typography variant='body2' sx={struckStyle}>
                                                {transaction.quantity}
                                              </Typography>
                                              {transaction.fillHistoryId &&
                                                editFillHistory &&
                                                !hasOverrides &&
                                                transaction.quantity >= 0 && (
                                                  <Tooltip title='Edit NDC' arrow placement='top'>
                                                    <IconButton
                                                      size='small'
                                                      onClick={handleEditNdc(transaction)}
                                                      sx={{
                                                        padding: '2px',
                                                        '&:hover': { cursor: 'pointer' },
                                                      }}
                                                    >
                                                      <EditIcon sx={{ fontSize: '16px' }} />
                                                    </IconButton>
                                                  </Tooltip>
                                                )}
                                            </Box>
                                          </TableCell>
                                          <TableCell align='center'>
                                            <Typography variant='body2' sx={struckStyle}>
                                              {reasonDisplay}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                        {hasOverrides &&
                                          transaction.overrides.map((override) => (
                                            <TableRow
                                              key={override.ndc}
                                              sx={{ backgroundColor: 'white' }}
                                            >
                                              <TableCell />
                                              <TableCell align='center'>
                                                <Typography
                                                  variant='body2'
                                                  sx={{ color: 'info.dark', fontWeight: 500 }}
                                                >
                                                  {override.ndc}
                                                </Typography>
                                              </TableCell>
                                              <TableCell align='center'>
                                                {transaction.isReshipment ? 'Yes' : 'No'}
                                              </TableCell>
                                              <TableCell align='center'>
                                                {getTransactionDate()}
                                              </TableCell>
                                              <TableCell align='center'>
                                                {isVoided ? 'Yes' : 'No'}
                                              </TableCell>
                                              <TableCell align='center'>
                                                {transaction.voidedTimestamp
                                                  ? format(
                                                      new Date(transaction.voidedTimestamp),
                                                      'MM/dd/yyyy'
                                                    )
                                                  : '-'}
                                              </TableCell>
                                              <TableCell
                                                align='center'
                                                sx={{
                                                  fontWeight: 500,
                                                  color:
                                                    override.quantity >= 0
                                                      ? 'success.main'
                                                      : 'error.main',
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  {override.quantity}
                                                  {transaction.fillHistoryId &&
                                                    editFillHistory &&
                                                    override.quantity >= 0 && (
                                                      <Tooltip
                                                        title='Edit NDC'
                                                        arrow
                                                        placement='top'
                                                      >
                                                        <IconButton
                                                          size='small'
                                                          onClick={handleEditNdc(transaction)}
                                                          sx={{
                                                            padding: '2px',
                                                            '&:hover': { cursor: 'pointer' },
                                                          }}
                                                        >
                                                          <EditIcon sx={{ fontSize: '16px' }} />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                </Box>
                                              </TableCell>
                                              <TableCell align='center'>{reasonDisplay}</TableCell>
                                            </TableRow>
                                          ))}
                                      </>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <Dialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Typography variant='h6'>Edit NDC</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {overRideRows.map((row) => (
              <Box key={row.id} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  {/* NDC field with dropdown */}
                  <Box sx={{ flex: 2, position: 'relative' }}>
                    <TextField
                      fullWidth
                      label='NDC*'
                      value={activeRowId === row.id ? ndcSearch : row.ndc}
                      onChange={(e) => handleRowNdcInput(row.id, e.target.value)}
                      onFocus={() => {
                        setActiveRowId(row.id);
                        setNdcSearch(row.ndc);
                      }}
                      placeholder={selectedTransaction?.ndc || 'Search NDC...'}
                      size='small'
                      error={row.ndc && ndcCounts[row.ndc] > 1}
                      InputProps={{
                        endAdornment: activeRowId === row.id && searchLoading && (
                          <InputAdornment position='end'>
                            <CircularProgress size={16} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    {/* Dropdown for this row */}
                    {activeRowId === row.id && drugProfiles.length > 0 && (
                      <Paper
                        sx={{
                          position: 'absolute',
                          zIndex: 1300,
                          width: '100%',
                          maxHeight: 200,
                          overflow: 'auto',
                          border: '1px solid',
                          borderColor: 'divider',
                          mt: 0.5,
                        }}
                        elevation={4}
                      >
                        <List dense>
                          {drugProfiles.map((drug) => (
                            <ListItem key={`${drug.ndc}-${drug.drugName}`} disablePadding>
                              <ListItemButton
                                selected={row.ndc === drug.ndc}
                                onMouseDown={() => handleSelectDrugForRow(row.id, drug)}
                              >
                                <ListItemText
                                  primary={drug.drugName}
                                  secondary={`NDC: ${drug.ndc}`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    )}
                    {row.ndc && ndcCounts[row.ndc] > 1 && (
                      <Typography
                        variant='caption'
                        color='error'
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        Duplicate NDC
                      </Typography>
                    )}
                    {activeRowId === row.id && ndcSearch.length > 0 && ndcSearch.length < 3 && (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        Type at least 3 characters
                      </Typography>
                    )}
                    {activeRowId === row.id &&
                      ndcSearch.length >= 3 &&
                      !searchLoading &&
                      drugProfiles.length === 0 && (
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          No drug profiles found
                        </Typography>
                      )}
                  </Box>

                  {/* Qty field */}
                  <TextField
                    label='Qty*'
                    value={row.quantity}
                    onChange={(e) => handleRowQuantityChange(row.id, e.target.value)}
                    type='number'
                    inputProps={{ min: 0, step: 'any' }}
                    size='small'
                    sx={{ flex: 1 }}
                  />

                  {/* Remove row button */}
                  {overRideRows.length > 1 && (
                    <IconButton
                      size='small'
                      onClick={() => handleRemoveRow(row.id)}
                      sx={{ mt: 0.5 }}
                    >
                      <CloseIcon sx={{ fontSize: '18px' }} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}

            {/* Add More NDC link */}
            <Button
              startIcon={<span style={{ fontSize: '18px', lineHeight: 1 }}>+</span>}
              onClick={handleAddRow}
              size='small'
              sx={{ textTransform: 'none', mt: 0.5, pl: 0, color: 'primary.main' }}
            >
              Add More NDC
            </Button>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            onClick={handleCloseEditDialog}
            disabled={updateLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateNdc}
            variant='contained'
            disabled={isSubmitDisabled || updateLoading}
          >
            {updateLoading ? <CircularProgress size={20} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

FillHistoryDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onUpdateFillHistory: PropTypes.func,
  fillHistory: PropTypes.shape({
    count: PropTypes.number,
    results: PropTypes.arrayOf(
      PropTypes.shape({
        fillHistoryId: PropTypes.string,
        id: PropTypes.string,
        prescriptionId: PropTypes.string,
        patientId: PropTypes.string,
        fillDate: PropTypes.string,
        quantity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lineItemId: PropTypes.string,
        fillType: PropTypes.string,
        isReshipment: PropTypes.bool,
        ndc: PropTypes.string,
        createdTimestamp: PropTypes.string,
        modifiedTimestamp: PropTypes.string,
        orderId: PropTypes.string,
        rxNumber: PropTypes.string,
        isVoided: PropTypes.bool,
        voidedTimestamp: PropTypes.string,
        parentOrderId: PropTypes.string,
      })
    ),
  }),
};

FillHistoryDialog.defaultProps = {
  fillHistory: null,
  loading: false,
  onUpdateFillHistory: null,
};

export default FillHistoryDialog;
